import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-333e9c3c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "chart-container" }

import { ref, onMounted, watch, nextTick, computed } from "vue";

// Определяем перечисление статусов
enum TaskStatus {
  Disabled = 0,
  Wait = 1,
  Queued = 2,
  Preparing = 3,
  RunWaiting = 4,
  Running = 5,
  Success = 6,
  Failed = 7,
  Removed = 8,
}

// Цвета для каждого статуса
interface StatusData {
  Status: TaskStatus;
  Count: number;
  startAngle?: number; // Опциональные поля для углов
  endAngle?: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DonutChart',
  props: {
    data: {}
  },
  setup(__props: any) {

const props = __props;

const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.Disabled]: "#660000",
  [TaskStatus.Wait]: "#cc5500",
  [TaskStatus.Queued]: "#365d6f",
  [TaskStatus.Preparing]: "#cc8400",
  [TaskStatus.RunWaiting]: "#009a9a",
  [TaskStatus.Running]: "#002eb8",
  [TaskStatus.Success]: "#29a329",
  [TaskStatus.Failed]: "#cc0000",
  [TaskStatus.Removed]: "#7a7a7a",
};

// Названия статусов
const STATUS_NAMES: Record<TaskStatus, string> = {
  [TaskStatus.Disabled]: "Отключено",
  [TaskStatus.Wait]: "Ожидание",
  [TaskStatus.Queued]: "В очереди",
  [TaskStatus.Preparing]: "Подготовка",
  [TaskStatus.RunWaiting]: "Ожидание запуска",
  [TaskStatus.Running]: "Выполняется",
  [TaskStatus.Success]: "Успешно",
  [TaskStatus.Failed]: "Ошибка",
  [TaskStatus.Removed]: "Удалено",
};



const canvas = ref<HTMLCanvasElement | null>(null);
const hoveredData = ref<StatusData | null>(null);
const tooltipStyle = ref({ top: "0px", left: "0px" });

const total = computed(() =>
  props.data.reduce((sum, item) => sum + item.Count, 0)
);

const getStatusName = (status: TaskStatus) => {
  return STATUS_NAMES[status] || `Статус ${status}`;
};

const getPercentage = (count: number) => {
  if (total.value === 0) return 0;
  return ((count / total.value) * 100).toFixed(1);
};

// Анимация и рисование диаграммы
const drawChart = (progress: number = 1) => {
  if (!canvas.value) return;

  const ctx = canvas.value.getContext("2d");
  if (!ctx) return;

  const width = (canvas.value.width = 150);
  const height = (canvas.value.height = 150);
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = 70;
  const innerRadius = 40;

  ctx.clearRect(0, 0, width, height);

  props.data.forEach((item) => {
    if (item.startAngle === undefined || item.endAngle === undefined) return;

    const sliceAngle = item.endAngle - item.startAngle;
    const interpolatedEnd = item.startAngle + sliceAngle * progress;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, item.startAngle, interpolatedEnd);
    ctx.arc(
      centerX,
      centerY,
      innerRadius,
      interpolatedEnd,
      item.startAngle,
      true
    );
    ctx.closePath();

    ctx.fillStyle = STATUS_COLORS[item.Status] || "#ccc";
    ctx.fill();
  });
};
const calculateAngles = () => {
  let startAngle = 0; // Без -Math.PI/2

  props.data.forEach((item) => {
    const sliceAngle = (item.Count / total.value) * 2 * Math.PI;
    const endAngle = startAngle + sliceAngle;

    item.startAngle = startAngle;
    item.endAngle = endAngle;

    startAngle = endAngle;
  });
};
const animateChart = () => {
  if (!canvas.value) return;

  calculateAngles(); // <<< Сначала точно рассчитываем углы

  let animationProgress = 0;
  const animationDuration = 1000;

  const drawFrame = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / animationDuration, 1);
    animationProgress = progress;

    drawChart(animationProgress);

    if (progress < 1) {
      requestAnimationFrame(drawFrame);
    } else {
      drawChart(1); // финальный рендер
    }
  };

  let startTime: number | null = null;
  requestAnimationFrame(drawFrame);
};

// Обработка наведения мыши
const handleMouseMove = (event: MouseEvent) => {
  if (!canvas.value) return;

  const rect = canvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  const centerX = canvas.value.width / 2;
  const centerY = canvas.value.height / 2;
  const radius = 70;
  const innerRadius = 40;

  const dx = x - centerX;
  const dy = y - centerY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Угол от 0 до 2π (нормализуем)
  let angle = Math.atan2(dy, dx);
  if (angle < 0) angle += 2 * Math.PI;

  if (distance >= innerRadius && distance <= radius) {
    hoveredData.value = null;

    for (const item of props.data) {
      if (item.startAngle !== undefined && item.endAngle !== undefined) {
        const start = item.startAngle;
        const end = item.endAngle;

        const isInside =
          end >= start
            ? angle >= start && angle <= end
            : angle >= start || angle <= end; // если сектор пересекает 0

        if (isInside) {
          hoveredData.value = item;
          tooltipStyle.value = {
            top: `${event.clientY - rect.top + 10}px`,
            left: `${event.clientX - rect.left + 10}px`,
          };
          break;
        }
      }
    }
  } else {
    hoveredData.value = null;
  }
};

const handleMouseLeave = () => {
  hoveredData.value = null;
};

onMounted(() => {
  nextTick(() => animateChart());
});

watch(
  () => props.data,
  () => {
    nextTick(() => animateChart());
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("canvas", {
      ref_key: "canvas",
      ref: canvas,
      onMousemove: handleMouseMove,
      onMouseleave: handleMouseLeave
    }, null, 544),
    (hoveredData.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "tooltip",
          style: _normalizeStyle(tooltipStyle.value)
        }, [
          _createElementVNode("span", {
            class: "tooltip-color",
            style: _normalizeStyle({
          backgroundColor: STATUS_COLORS[hoveredData.value.Status] || '#ccc',
        })
          }, null, 4),
          _createTextVNode(" " + _toDisplayString(getStatusName(hoveredData.value.Status)) + ": " + _toDisplayString(hoveredData.value.Count) + " (" + _toDisplayString(getPercentage(hoveredData.value.Count)) + "%) ", 1)
        ], 4))
      : _createCommentVNode("", true)
  ]))
}
}

})